/**
 * Turning a `LAYOUT_PROBLEM` code into a sentence a partner can act on.
 *
 * Same split as `visual/issueMessages.ts`: `LayoutProblem.detail` is
 * developer-facing English naming the tag and the line — which is what makes it
 * useful — so the UI shows a translated sentence for the `code` and keeps the
 * detail beside it as technical text.
 *
 * The copy names constructs in words rather than showing them: to react-intl a
 * `{` opens an ICU argument and `<html>` opens a rich-text tag, so a message
 * carrying literal Twig or markup fails to parse at all.
 */
import { LAYOUT_PROBLEM } from './layoutValidation';

export interface LayoutProblemMessage {
  id: string;
  defaultMessage: string;
}

const MESSAGES: Record<string, LayoutProblemMessage> = {
  [LAYOUT_PROBLEM.NO_CONTENT_SLOT]: {
    id: 'pages.emailCenter.layout.problem.noContentSlot',
    defaultMessage:
      'This layout has nowhere for an email’s content to go, so every email using it would arrive with only the layout. Add a Twig block named “content” where the email’s content belongs.',
  },
  [LAYOUT_PROBLEM.UNCONVENTIONAL_SLOT]: {
    id: 'pages.emailCenter.layout.problem.unconventionalSlot',
    defaultMessage:
      'Emails put their content in a block named “content” (or “body”). This layout declares neither, so their content would be left out.',
  },
  [LAYOUT_PROBLEM.NOT_A_DOCUMENT]: {
    id: 'pages.emailCenter.layout.problem.notADocument',
    defaultMessage:
      'A layout is the whole email document, so it needs an html element and a body element around the content.',
  },
  [LAYOUT_PROBLEM.MISSING_DOCTYPE]: {
    id: 'pages.emailCenter.layout.problem.missingDoctype',
    defaultMessage:
      'Without a doctype line at the top, several email clients render the layout in quirks mode and spacing shifts.',
  },
  [LAYOUT_PROBLEM.UNCLOSED_TAG]: {
    id: 'pages.emailCenter.layout.problem.unclosedTag',
    defaultMessage:
      'An element is never closed. The layout wraps every email, so each client would decide for itself where it ends.',
  },
  [LAYOUT_PROBLEM.STRAY_CLOSING_TAG]: {
    id: 'pages.emailCenter.layout.problem.strayClosingTag',
    defaultMessage: 'A closing tag has nothing left to close.',
  },
  [LAYOUT_PROBLEM.MISNESTED_TAG]: {
    id: 'pages.emailCenter.layout.problem.misnestedTag',
    defaultMessage:
      'Two elements overlap instead of nesting, so clients repair the markup differently from one another.',
  },
  [LAYOUT_PROBLEM.UNCLOSED_TWIG_TAG]: {
    id: 'pages.emailCenter.layout.problem.unclosedTwigTag',
    defaultMessage:
      'A Twig tag is never closed, so every email using this layout would fail to render.',
  },
  [LAYOUT_PROBLEM.STRAY_TWIG_END]: {
    id: 'pages.emailCenter.layout.problem.strayTwigEnd',
    defaultMessage:
      'A Twig end tag closes something that was never opened, so every email using this layout would fail to render.',
  },
};

/**
 * The message for a code, or null when there is none — the caller then shows
 * `detail` verbatim rather than mislabelling it with a reassuring sentence.
 */
export function layoutProblemMessage(
  code: string,
): LayoutProblemMessage | null {
  return MESSAGES[code] ?? null;
}
